import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  isValidElement,
  cloneElement,
  Ref,
  ReactElement,
} from 'react';

import { Tooltip } from '@mui/material';

interface FullTextTooltipProps {
  children: ReactNode;
}

export const FullTextTooltip = ({ children }: FullTextTooltipProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const fallbackRef = useRef<HTMLElement>(null);

  // Determine which ref to use: child's existing ref or our fallback we attach to cloned element
  const getElementRef = () => {
    if (isValidElement(children)) {
      const childRef = (children as unknown as { ref?: Ref<HTMLElement> }).ref;
      if (childRef && typeof childRef === 'object' && 'current' in childRef) {
        return childRef;
      }
    }
    return fallbackRef;
  };

  const elementRef = getElementRef();

  const checkOverflow = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    // Check overflow on the wrapper span first
    let isTextOverflowing = element.scrollWidth > element.clientWidth;

    // If no overflow on wrapper, check the first child element only
    if (!isTextOverflowing && element.firstElementChild) {
      const firstChild = element.firstElementChild as HTMLElement;
      isTextOverflowing =
        firstChild.scrollWidth > firstChild.clientWidth ||
        firstChild.scrollHeight > firstChild.clientHeight;
    }

    setIsOverflowing(isTextOverflowing);
  }, []);

  useEffect(() => {
    // Extract text content for tooltip
    const element = elementRef.current;
    if (element) {
      setTooltipText(element.textContent || element.innerText || '');
    }

    // Initial check after render
    const timeoutId = setTimeout(checkOverflow, 0);

    // Set up ResizeObserver for dynamic size changes
    if (!element || !ResizeObserver) {
      return () => clearTimeout(timeoutId);
    }

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [checkOverflow, children]);

  if (!children) {
    return null;
  }

  let childWithRef: ReactNode;
  if (isValidElement(children) && typeof children.type !== 'string') {
    // Custom React component - only attach ref if it doesn't have one
    const existingRef = (children as unknown as { ref?: Ref<HTMLElement> }).ref;
    if (existingRef) {
      // Use the existing ref, no need to clone
      childWithRef = children;
    } else {
      // Attach our fallback ref
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      childWithRef = cloneElement(children as ReactElement<any>, {
        ref: fallbackRef,
      });
    }
  } else if (isValidElement(children) && typeof children.type === 'string') {
    // Native element - only attach ref if it doesn't have one
    const existingRef = (children as unknown as { ref?: Ref<HTMLElement> }).ref;

    if (existingRef) {
      // Use the existing ref, don't modify the element
      childWithRef = children;
    } else {
      // Attach our fallback ref
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      childWithRef = cloneElement(children as ReactElement<any>, {
        ref: fallbackRef,
      });
    }
  } else {
    // Fallback: wrap in a span, with maxWidth 100% to not interfere with layout, and attach ref there
    childWithRef = (
      <span ref={fallbackRef} style={{ maxWidth: '100%' }}>
        {children}
      </span>
    );
  }

  return (
    <Tooltip
      title={tooltipText}
      disableHoverListener={!isOverflowing && !!tooltipText}
      disableFocusListener={!isOverflowing && !!tooltipText}
      disableTouchListener={!isOverflowing && !!tooltipText}
    >
      {childWithRef}
    </Tooltip>
  );
};
